import { useState, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../config/firebase';
import { collection, addDoc, serverTimestamp, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';

export const useRecording = (userId, userProfile) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Pro tier advanced features
  const [chapters, setChapters] = useState([]); // { time: number, title: string }
  const [highlights, setHighlights] = useState([]); // { time: number, duration: number, label: string }
  const [speakerEvents, setSpeakerEvents] = useState([]); // { time: number, speakerId: string, volume: number }

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const audioAnalyserRef = useRef(null);

  const isPremium = userProfile?.isPremium || userProfile?.subscriptionTier === 'pro' || userProfile?.subscriptionTier === 'team';
  const maxRecordings = isPremium ? 10 : 1;
  const maxDuration = 3600; // 1 hour in seconds
  const quality = isPremium ? '1080p' : '720p';
  const videoBitsPerSecond = isPremium ? 5000000 : 2500000; // 5Mbps vs 2.5Mbps

  // Check if user can start recording
  const canStartRecording = async () => {
    try {
      const recordingsRef = collection(db, 'users', userId, 'recordings');
      const snapshot = await getDocs(recordingsRef);

      if (snapshot.size >= maxRecordings) {
        return {
          allowed: false,
          reason: `You've reached your recording limit (${maxRecordings} recording${maxRecordings > 1 ? 's' : ''}). Delete an old recording to record again.`
        };
      }

      return { allowed: true };
    } catch (error) {
      return { allowed: true }; // Fail open to avoid blocking users
    }
  };

  const startRecording = async (canvasOrStream) => {
    // Check quota first
    const check = await canStartRecording();
    if (!check.allowed) {
      return { success: false, message: check.reason };
    }

    try {
      // Get stream - either directly provided or from canvas
      const stream = canvasOrStream instanceof MediaStream
        ? canvasOrStream
        : canvasOrStream.captureStream(isPremium ? 30 : 30);

      // Check for codec support
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';

      const options = {
        mimeType,
        videoBitsPerSecond,
        audioBitsPerSecond: 128000 // 128kbps for audio
      };

      console.log('🎬 Starting MediaRecorder with options:', {
        mimeType,
        videoBitsPerSecond,
        audioBitsPerSecond: 128000,
        hasVideoTrack: stream.getVideoTracks().length > 0,
        hasAudioTrack: stream.getAudioTracks().length > 0,
        videoTracks: stream.getVideoTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled })),
        audioTracks: stream.getAudioTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled }))
      });

      mediaRecorderRef.current = new MediaRecorder(stream, options);
      recordedChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.start(1000); // Collect data every second
      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        // Auto-stop at max duration
        if (elapsed >= maxDuration) {
          stopRecording();
        }
      }, 1000);

      return { success: true };
    } catch (error) {
      console.error('Error starting recording:', error);
      return { success: false, message: 'Failed to start recording. Please try again.' };
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || !isRecording) {
        resolve(null);
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        clearInterval(timerIntervalRef.current);
        setIsRecording(false);

        // Cleanup audio analyser
        if (audioAnalyserRef.current) {
          audioAnalyserRef.current.audioContext?.close();
          audioAnalyserRef.current = null;
        }

        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log('Recording blob created:', {
          size: blob.size,
          sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
          chunks: recordedChunksRef.current.length,
          duration: recordingTime,
          chapters: chapters.length,
          highlights: highlights.length
        });
        resolve(blob);
      };

      // Request final data before stopping
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.requestData();
        setTimeout(() => {
          mediaRecorderRef.current.stop();
        }, 100); // Small delay to ensure requestData completes
      } else {
        mediaRecorderRef.current.stop();
      }
    });
  };

  const uploadRecording = async (blob, meetingId, participants, meetingName = null) => {
    if (!blob) return null;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const recordingId = `recording-${Date.now()}`;
      const timestamp = new Date().toISOString().split('T')[0];

      // Use meeting name if provided, otherwise use meeting ID
      const sanitizedName = meetingName
        ? meetingName.replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, '-')
        : `meeting-${meetingId}`;
      const fileName = `${sanitizedName}-${timestamp}.webm`;
      const storagePath = `recordings/${userId}/${recordingId}.webm`;

      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, blob);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(Math.round(progress));
          },
          (error) => {
            console.error('Upload error:', error);
            setIsUploading(false);
            reject(error);
          },
          async () => {
            try {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

              // Save metadata to Firestore
              const recordingsRef = collection(db, 'users', userId, 'recordings');
              const docRef = await addDoc(recordingsRef, {
                recordingId,
                meetingId,
                meetingName: meetingName || `Meeting ${meetingId}`,
                fileName,
                storagePath,
                downloadUrl,
                fileSize: blob.size,
                duration: recordingTime,
                quality,
                participants: participants || [],
                // Pro tier metadata
                chapters: isPremium ? chapters : [],
                highlights: isPremium ? highlights : [],
                speakerEvents: isPremium ? speakerEvents : [],
                createdAt: serverTimestamp()
              });

              // Update recording count cache
              const userRef = doc(db, 'users', userId);
              const userDoc = await getDoc(userRef);
              const currentCount = userDoc.data()?.recordingCount || 0;
              await updateDoc(userRef, { recordingCount: currentCount + 1 });

              setIsUploading(false);
              setUploadProgress(0);
              setRecordingTime(0);

              // Reset Pro tier metadata for next recording
              setChapters([]);
              setHighlights([]);
              setSpeakerEvents([]);

              resolve({ downloadUrl, recordingId, docId: docRef.id });
            } catch (error) {
              setIsUploading(false);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      setIsUploading(false);
      throw error;
    }
  };

  // Pro tier: Add chapter marker
  const addChapter = (title) => {
    if (!isPremium || !isRecording) return;

    const chapter = {
      time: recordingTime,
      title: title || `Chapter ${chapters.length + 1}`,
      timestamp: new Date().toISOString()
    };

    setChapters(prev => [...prev, chapter]);
    console.log('📑 Chapter added:', chapter);
    return chapter;
  };

  // Pro tier: Add highlight moment
  const addHighlight = (label, duration = 5) => {
    if (!isPremium || !isRecording) return;

    const highlight = {
      time: recordingTime,
      duration,
      label: label || 'Important moment',
      timestamp: new Date().toISOString()
    };

    setHighlights(prev => [...prev, highlight]);
    console.log('⭐ Highlight added:', highlight);
    return highlight;
  };

  // Pro tier: Detect active speaker (simplified volume-based detection)
  const detectActiveSpeaker = (audioStream, speakerId) => {
    if (!isPremium || !isRecording) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(audioStream);

      source.connect(analyser);
      analyser.fftSize = 256;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      audioAnalyserRef.current = { audioContext, analyser };

      // Check audio levels every 100ms
      const checkVolume = () => {
        if (!isRecording) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;

        // Record speaker event if volume above threshold
        if (average > 30) {
          setSpeakerEvents(prev => [...prev, {
            time: recordingTime,
            speakerId,
            volume: average,
            timestamp: new Date().toISOString()
          }]);
        }

        setTimeout(checkVolume, 100);
      };

      checkVolume();
    } catch (error) {
    }
  };

  // Pro tier: Remove chapter
  const removeChapter = (index) => {
    if (!isPremium) return;
    setChapters(prev => prev.filter((_, i) => i !== index));
  };

  // Pro tier: Remove highlight
  const removeHighlight = (index) => {
    if (!isPremium) return;
    setHighlights(prev => prev.filter((_, i) => i !== index));
  };

  // Pro tier: Get recording metadata
  const getRecordingMetadata = () => {
    return {
      chapters,
      highlights,
      speakerEvents,
      duration: recordingTime,
      quality,
      isPremium
    };
  };

  return {
    isRecording,
    recordingTime,
    uploadProgress,
    isUploading,
    startRecording,
    stopRecording,
    uploadRecording,
    canStartRecording,
    maxRecordings,
    maxDuration,

    // Pro tier advanced features
    chapters,
    highlights,
    speakerEvents,
    addChapter,
    addHighlight,
    removeChapter,
    removeHighlight,
    detectActiveSpeaker,
    getRecordingMetadata,
    isPremium
  };
};
